import { Component, inject, OnInit, TemplateRef, ViewChild } from '@angular/core'
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms'
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { map, Observable, Subject, take, takeUntil } from 'rxjs'
import { Role } from '../../../store/role-permission/role-permission.state'
import { selectRoles } from '../../../store/role-permission/role-permission.reducer'
import { selectTotalCount } from '../../../store/role-permission/role-permission.reducer'
import { CommonModule, NgIf } from '@angular/common'
import { RouterModule } from '@angular/router'
import { CapitalizePipe } from '../../../pipes/capitalize.pipe'
import { BlockUnblockComponent } from '../template/block-unblock/block-unblock.component'
import { PaginationComponent } from '../template/pagination/pagination.component'
import { DeleteComponent } from '../template/delete/delete.component'
import {
  blockRole,
  createRole,
  createRoleSuccess,
  // deleteRole,
  loadRoles,
  resetRoleState,
  updateRole,
  updateRoleSuccess
} from '../../../store/role-permission/role-permission.actions'
import { Business } from '../../../store/business/business.state'
import { selectAllBusinesses } from '../../../store/business/business.selectors'
import { loadBusinesses } from '../../../store/business/business.actions'
import { eps } from '../../../services/url.service'
import { AuthState } from '../../../store/auth/auth.state'
import { selectUser } from '../../../store/auth/auth.selectors'
import { Actions, ofType } from '@ngrx/effects'
import { NgSelectModule } from '@ng-select/ng-select'
import { FormatModuleNamePipe } from '../../../pipes/format-module-name.pipe'
import { UtilsService } from '../../../services/utils.service'
import { noSpacesValidator } from '../../../validators/no-spaces.validators'
import { noLeadingTrailingSpaces } from '../../../validators/no-leadingTraillingSpace.validators'
import { SharedModule } from '../../../shared/shared.module'

// interface Permissions {
//   view: boolean
//   add: boolean
//   edit: boolean
//   delete: boolean
// }

type PermissionAction = 'status' | 'view' | 'add' | 'edit' | 'delete'

type Permissions = Partial<Record<PermissionAction, boolean>>

@Component({
  selector: 'app-role-permission',
  imports: [
    ReactiveFormsModule,
    NgIf,
    CommonModule,
    RouterModule,
    CapitalizePipe,
    BlockUnblockComponent,
    FormsModule,
    DeleteComponent,
    PaginationComponent,
    NgSelectModule,
    // BrowserAnimationsModule,
    FormatModuleNamePipe,
    SharedModule
  ],
  templateUrl: './role-permission.component.html',
  styleUrl: './role-permission.component.scss'
})
export class RolePermissionComponent implements OnInit {
  private store = inject(Store) // Inject store using `inject()`
  eps = eps
  loading$: Observable<boolean> = this.store.select(state => state.role.loading)
  error$: Observable<string | null> = this.store.select(state => state.role.error)
  pageLastRecord = 0
  totalCount$!: Observable<number>
  totalCount = 0
  search = ''
  blockRoleId = ''
  showModal = false
  showDeleteModal = false
  isBlocking!: boolean // Assuming this will be set before opening the modal
  roles$: Observable<Role[]> = this.store.select(selectRoles)
  currentPage = 1
  pageLimit = 10
  businesses$: Observable<Business[]> = this.store.select(selectAllBusinesses)
  @ViewChild('addBusinessModals') addBusinessModals!: TemplateRef<any>
  private modalRef!: NgbModalRef
  modalTitle: any
  isStandard = false
  isPermissionSets = false
  form!: FormGroup
  permissionModules: {
    key: string
    name: string
    permissions: PermissionAction[]
  }[] = [
    {
      key: 'business',
      name: 'Business Entity',
      permissions: ['view', 'add', 'edit', 'delete']
    },
    { key: 'office', name: 'Office', permissions: ['view', 'add', 'edit', 'delete'] },
    { key: 'designation', name: 'Designation', permissions: ['view', 'add', 'edit', 'delete'] },
    { key: 'department', name: 'Department', permissions: ['view', 'add', 'edit', 'delete'] },
    // { key: 'user', name: 'User', permissions: ['view', 'add', 'edit', 'delete'] },
    { key: 'employees', name: 'Employees', permissions: ['view', 'add', 'edit', 'delete'] },

    { key: 'holidays', name: 'Holidays', permissions: ['view', 'add', 'edit', 'delete'] },
    { key: 'leaves', name: 'Leaves', permissions: ['view', 'add', 'edit', 'delete'] },
    {
      key: 'leaveRequests',
      name: 'Leave Requests',
      permissions: ['view', 'add', 'edit', 'delete']
    },
    // { key: 'jobs', name: 'Jobs', permissions: ['view', 'add', 'edit', 'delete'] },
    // { key: 'candidate', name: 'Candidate', permissions: ['view', 'add', 'edit', 'delete'] },
    { key: 'attendance', name: 'Attendance', permissions: ['view', 'add', 'edit', 'delete'] },
    { key: 'userRole', name: 'Role & Permission', permissions: ['view', 'add', 'edit', 'delete'] },
    {
      key: 'attendancePolicy',
      name: 'Attendance Policy',
      permissions: ['view', 'add', 'edit', 'delete']
    },
    { key: 'leavePolicy', name: 'Leave Policy', permissions: ['view', 'add', 'edit', 'delete'] },
    {
      key: 'attendanceList',
      name: 'Attendance List',
      permissions: ['view', 'add', 'edit', 'delete']
    },
    // {
    //   key: 'standardEarnings',
    //   name: 'Standard Earnings',
    //   permissions: ['view', 'add', 'edit', 'delete']
    // },
    {
      key: 'customEarnings',
      name: 'Custom Earnings',
      permissions: ['view', 'add', 'edit', 'delete']
    },
    {
      key: 'customDeductions',
      name: 'Custom Deductions',
      permissions: ['view', 'add', 'edit', 'delete']
    },
    // {
    //   key: 'statutoryDeductions',
    //   name: 'Statutory Deductions',
    //   permissions: ['view', 'add', 'edit', 'delete']
    // },
    {
      key: 'reimbursements',
      name: 'Reimbursements',
      permissions: ['view', 'add', 'edit', 'delete']
    },
    {
      key: 'employeeSalary',
      name: 'Employee Salary',
      permissions: ['view', 'add', 'edit', 'delete']
    }

    // Add more modules as needed
  ]

  // permissionsState: { [key: string]: { [key: string]: boolean } } = {};
  // permissionsState: { [key: string]: Permissions } = {}
  permissionsState: {
    [key: string]: Partial<Record<PermissionAction, boolean>>
  } = {}
  user: AuthState['user'] | null = null
  businessIdss!: string[]
  // businessId = ''
  businessId: string | null = null

  status = ''
  roletype = 'Role'
  private destroyed$ = new Subject<void>()
  readonly systemRoles = ['Admin', 'Business Owner', 'Employee']

  constructor(
    private modalService: NgbModal,
    private fb: FormBuilder,
    private actions$: Actions,
    private utilService: UtilsService
  ) {
    this.store.dispatch(resetRoleState())
    this.roles$ = this.store.select(selectRoles)
    this.totalCount$ = this.store.select(selectTotalCount).pipe(
      map(totalCount => totalCount || 0) // Default to 0 if totalCount is null
    )

    // Subscribe to totalCount$ safely
    this.totalCount$.subscribe(count => {
      this.totalCount = count // Define this.totalCount in your class
    })
    // this.store.dispatch(loadBusinesses({ params: null }))

    this.permissionModules.forEach(module => {
      this.permissionsState[module.name] = {
        view: this.isChecked(module.permissions, ''),
        add: this.isChecked(module.permissions, ''),
        edit: this.isChecked(module.permissions, ''),
        delete: this.isChecked(module.permissions, '')
      }
    })

    this.store
      .select(selectUser)
      .pipe(take(1))
      .subscribe(user => {
        this.user = user

        //this.selectedBusiness = user?.business?.id

        // this.businessList = user?.businesses
        //if (this.user?.role.name === 'Business Owner') {
        //  this.selectedBusinessOwner = this.user.businessOwnerId
        //}
        // this.getAttendancePolicyList()
        const businessIds = user?.businessIds // your array of business IDs
        // const paramString = `ids=${businessIds?.join(',') ?? ''}`;
        if (businessIds && businessIds.length) {
          this.businessIdss = businessIds
          const paramString = `businessIds=${businessIds.join(',')}`
          this.store.dispatch(loadBusinesses({ params: paramString }))
        } else {
          this.loadAllBusinesses() // Load all businesses initially
          // this.loadAllBusinessOwners()
        }
      })
  }

  ngOnInit() {
    this.loadRoles()

    this.totalCount$.subscribe(count => {
      this.totalCount = count
      this.updatePageLastRecord()
    })

    this.form = this.fb.group({
      businessId: [{ value: null, disabled: false }, Validators.required],
      name: ['', [Validators.required, Validators.maxLength(250), noLeadingTrailingSpaces()]],
      permissions: [''],
      isStandard: [false],
      isPermissionSets: ['Role']
    })

    this.actions$
      .pipe(
        ofType(createRoleSuccess, updateRoleSuccess),
        takeUntil(this.destroyed$) // don't forget to clean up
      )
      .subscribe(() => {
        this.loadRoles()
        this.modalService.dismissAll() // or modalRef.close() if you're using one
        this.form.reset()
        // console.log('log permission calling')
        this.resetPermissions()
      })

    this.form.get('name')?.valueChanges.subscribe(name => {
      this.checkSystemRoleAndAdjustValidators(name, this.form.get('isStandard')?.value)
    })

    this.form.get('isStandard')?.valueChanges.subscribe(isStandard => {
      this.checkSystemRoleAndAdjustValidators(this.form.get('name')?.value, isStandard)
    })
  }

  loadRoles() {
    const params: any = {}
    params.roleType = 'Role'
    if (this.businessIdss?.length) {
      params.businessIds = this.businessIdss.join(',')
    }

    if (this.search) {
      params.name = this.search
    }

    if (this.status) {
      const isDisabled = this.status === 'true' ? true : this.status === 'false' ? false : null
      params.disabled = isDisabled
    }

    if (this.isStandard) {
      params.isStandard = this.isStandard
    }

    // if (this.roletype) {
    //   params.roleType = this.roletype
    // }

    if (this.businessId) {
      params.businessId = this.businessId
    }

    this.store.dispatch(
      loadRoles({
        params: new URLSearchParams(params).toString(),
        page: this.currentPage,
        limit: this.pageLimit
      })
    )
  }

  onSearch() {
    this.currentPage = 1 // Reset to the first page on search
    const params = this.search ? `name=${encodeURIComponent(this.search)}` : null

    this.store.dispatch(loadRoles({ params, page: this.currentPage, limit: this.pageLimit })) // Dispatch action with filter
  }

  onPageChange(newPage: number): void {
    if (newPage > 0) {
      this.currentPage = newPage
      this.loadRoles() // Reload businesses for the new page
      this.updatePageLastRecord()
    }
  }

  private updatePageLastRecord() {
    this.pageLastRecord = Math.min(this.currentPage * this.pageLimit, this.totalCount)
  }

  onPageLimitChange() {
    this.currentPage = 1 // Reset to first page when limit changes
    this.loadRoles() // Load employees with new page limit
  }

  onBusinessSelect(businessId: string) {
    if (businessId) {
      // Call a method to fetch data based on the selected owner
      const params = `businessId=${encodeURIComponent(businessId)}`

      this.store.dispatch(loadRoles({ params, page: this.currentPage, limit: this.pageLimit })) // Dispatch action with filter
    } else {
      const params = ''
      this.store.dispatch(loadRoles({ params, page: this.currentPage, limit: this.pageLimit })) // Dispatch action with filter
      // Handle the case where no owner is selected
      console.log('No business selected')
    }
  }

  onStatusSelect(status: string) {
    if (status) {
      // Call a method to fetch data based on the selected owner
      const isDisabled = status === 'true' ? true : status === 'false' ? false : null

      const params = isDisabled !== null ? `disabled=${isDisabled}` : ''

      this.store.dispatch(loadRoles({ params, page: this.currentPage, limit: this.pageLimit })) // Dispatch action with filter
    } else {
      const params = ''
      this.store.dispatch(loadRoles({ params, page: this.currentPage, limit: this.pageLimit })) // Dispatch action with filter
      // Handle the case where no owner is selected
      // console.log('No status selected')
    }
  }

  onStandardSelect(status: string) {
    if (status) {
      // Call a method to fetch data based on the selected owner
      const isStandard = status === 'true' ? true : status === 'false' ? false : null

      const params = isStandard !== null ? `isStandard=${isStandard}` : ''

      this.store.dispatch(loadRoles({ params, page: this.currentPage, limit: this.pageLimit })) // Dispatch action with filter
    } else {
      const params = ''
      this.store.dispatch(loadRoles({ params, page: this.currentPage, limit: this.pageLimit })) // Dispatch action with filter
      // Handle the case where no owner is selected
      console.log('No status selected')
    }
  }

  onRoletypeSelect(businessId: string) {
    if (businessId) {
      // Call a method to fetch data based on the selected owner
      const params = `businessId=${encodeURIComponent(businessId)}`

      this.store.dispatch(loadRoles({ params, page: this.currentPage, limit: this.pageLimit })) // Dispatch action with filter
    } else {
      const params = ''
      this.store.dispatch(loadRoles({ params, page: this.currentPage, limit: this.pageLimit })) // Dispatch action with filter
      // Handle the case where no owner is selected
      // console.log('No business selected')
    }
  }

  openModal(isBlocking: boolean, roleId: string) {
    const modalRef = this.modalService.open(BlockUnblockComponent)
    modalRef.componentInstance.isBlocking = isBlocking
    modalRef.componentInstance.recordId = roleId
    // this.blockBusinessId = businessId

    modalRef.result.then(
      result => {
        // Handle the result from the modal
        if (result) {
          // Call the service to update the business status
          this.handleConfirm(result)
        }
      },
      reason => {
        // Handle the dismiss case
        if (reason) {
          this.handleConfirm(reason)
        }
      }
    )
  }

  handleConfirm(data: { id: string; status: boolean }) {
    this.store.dispatch(blockRole({ id: data.id, disabled: data.status }))
  }

  // openDeleteModal(roleId: string) {
  //   const modalRef = this.modalService.open(DeleteComponent)
  //   modalRef.componentInstance.recordId = roleId
  //   modalRef.result.then(
  //     result => {
  //       // Handle the result from the modal
  //       if (result) {
  //         // Call the service to update the business status
  //         this.handleDeleteConfirm(result)
  //       }
  //     },
  //     reason => {
  //       // Handle the dismiss case
  //       if (reason) {
  //         this.handleDeleteConfirm(reason)
  //       }
  //     }
  //   )
  // }

  // handleDeleteConfirm(data: { id: string }) {
  //   this.store.dispatch(deleteRole({ id: data.id }))
  // }

  openBusinessModal(content: TemplateRef<any>, options: any = {}, title: string) {
    this.modalTitle = title
    this.modalRef = this.modalService.open(content, {
      scrollable: false,
      centered: true,
      size: 'lg',
      ...options
    })

    // Pre-fill form if in edit mode
    if (options) {
      this.blockRoleId = options.id
      this.setPermissionsState(options.permissions)
      // this.modalTitle = `Edit Role`
      this.modalTitle = title ?? 'Edit Role'

      this.form.patchValue({
        name: options.name,
        status: options.status,
        isStandard: options.isStandard,
        businessId: options.businessId
        // permissions: options.permissions // assuming you have logic to map this
      })
    } else {
      this.form.reset() // if you're creating a new one
    }
  }

  onClose() {
    // if (this.form) {
    //   this.form.reset(); // Reset the form if it is defined
    // }
    // this.modalService.dismissAll();
    // this.currentStep = 1;

    try {
      this.resetPermissions()
      if (this.form) {
        this.form.reset()
      }
      if (this.modalRef) {
        this.modalRef.close()
      }
      this.modalService.dismissAll()
    } catch (error) {
      console.error('Error closing modal:', error)
    }
  }
  getErrorMessage(controlName: string): string | null {
    const control = this.form.get(controlName)
    // console.log('control name', control)
    if (control && control.touched && control.invalid) {
      if (control.hasError('required')) {
        return 'This field is required'
      }
    }
    return null // No error
  }

  onSubmit() {
    if (this.form.invalid) return

    const transformedPermissions = this.getTransformedPermissions()
    // let roleType = ''
    // if (this.modalTitle === 'Create Role' || this.modalTitle === 'Edit Role') {
    //   roleType = 'Role'
    // }
    // if (
    //   this.modalTitle === 'Create Permission Sets' ||
    //   this.modalTitle === 'Edit Permission Sets'
    // ) {
    //   roleType = 'Permission Sets'
    //   this.isStandard = false
    // }
    if (this.form.get('isStandard')?.value === null || undefined) {
      this.isStandard = false
      this.form.get('isStandard')?.setValue(false)
    }
    if (this.form.valid && !this.blockRoleId) {
      this.store.dispatch(
        createRole({
          name: this.form.get('name')?.value,
          permissions: transformedPermissions, // ✅ stringify the object
          status: 'Active',
          roleType: 'Role',
          isStandard: this.form.get('isStandard')?.value,
          businessId: this.form.get('businessId')?.value
        })
      )
    } else if (this.form.valid && this.blockRoleId) {
      this.store.dispatch(
        updateRole({
          id: this.blockRoleId,
          name: this.form.get('name')?.value,
          permissions: transformedPermissions, // ✅ stringify the object
          status: 'Active',
          roleType: 'Role',
          isStandard: this.form.get('isStandard')?.value,
          businessId: this.form.get('businessId')?.value
        })
      )
    }
    this.blockRoleId = ''
  }

  getChecked(event: Event): boolean {
    const target = event.target as HTMLInputElement
    return target ? target.checked : false // Default to false if target is null
  }

  // togglePermission(module: any, action: PermissionAction, isChecked: boolean): void {
  //   this.permissionsState[module.name][action] = isChecked
  // }
  // togglePermission(module: any, action: string, isChecked: boolean) {
  //   const key = module.key

  //   if (!this.permissionsState[key]) {
  //     this.permissionsState[key] = {}
  //   }

  //   this.permissionsState[key][action as PermissionAction] = isChecked
  // }

  // checkReadCheckbox(module: any): void {
  //   const moduleName = module.name
  //   const permissions = this.permissionsState[moduleName]
  //   this.permissionsState[moduleName].view = ['add', 'edit', 'delete', 'view'].some(
  //     action => permissions[action]
  //   )
  // }

  isChecked(permissions: string[], action: string): boolean {
    return permissions.includes(action)
  }

  toggleIsStandard() {
    this.isStandard = !this.isStandard
    if (this.modalTitle === 'Create Role' || this.modalTitle === 'Edit Role') {
      this.form.get('isStandard')?.setValue(this.isStandard)
    } else {
      this.form.get('isStandard')?.setValue(false)
    }
  }

  getTransformedPermissions(): Record<string, PermissionAction[]> {
    const result: Record<string, PermissionAction[]> = {}

    Object.entries(this.permissionsState).forEach(([moduleName, actions]) => {
      const allowedActions = Object.entries(actions)
        .filter(([_, isAllowed]) => isAllowed) // filter true permissions
        .map(([action]) => action as PermissionAction) // cast to proper type

      if (allowedActions.length > 0) {
        const camelCaseName = this.toCamelCase(moduleName)
        result[camelCaseName] = allowedActions
      }
    })

    return result
  }

  private toCamelCase(name: string): string {
    return name
      .replace(/[^a-zA-Z0-9 ]/g, '') // remove special characters
      .replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) =>
        index === 0 ? match.toLowerCase() : match.toUpperCase()
      )
      .replace(/\s+/g, '')
  }

  setPermissionsState(permissions: Record<string, string[]>) {
    Object.entries(permissions).forEach(([module, actions]) => {
      this.permissionsState[module] = {
        view: actions.includes('view'),
        add: actions.includes('add'),
        edit: actions.includes('edit'),
        delete: actions.includes('delete'),
        status: actions.includes('status')
      }
    })
  }

  loadAllBusinesses() {
    const params: any = {}
    if (this.businessIdss?.length) {
      params.businessIds = this.businessIdss.join(',')
    }

    this.store.dispatch(
      loadBusinesses({
        params: new URLSearchParams(params).toString()
      })
    )
  }

  isAdmin(): boolean {
    // return this.user?.role === 'Admin'
    return this.user?.userRoles?.some((ur: any) => ur.role?.name === 'Admin') ?? false
  }

  // resetPermissions() {
  //   this.permissionModules.forEach(module => {
  //     this.permissionsState[module.name] = {
  //       view: false,
  //       add: false,
  //       edit: false,
  //       delete: false
  //     }
  //   })
  // }

  resetPermissions() {
    this.permissionsState = {}

    // Optional: reset all permissions to false
    this.permissionModules.forEach(module => {
      this.permissionsState[module.key] = {}
      module.permissions.forEach(action => {
        this.permissionsState[module.key][action] = false
      })
    })
  }

  get isSystemRole(): boolean {
    const name = this.form.get('name')?.value
    // console.log('name', this.systemRoles.includes(name))
    return this.systemRoles.includes(name)
  }

  checkSystemRoleAndAdjustValidators(name: string, isStandard: boolean) {
    const systemRoles = ['Admin', 'Business Owner', 'Employee']
    const businessIdControl = this.form.get('businessId')
    const nameControl = this.form.get('businessId')
    const isStandardControl = this.form.get('isStandard')
    const isStandardValue = this.form.get('isStandard')?.value
    console.log('is standard', isStandardValue)
    if (systemRoles.includes(name) && isStandardValue === true) {
      businessIdControl?.clearValidators()
      businessIdControl?.setValue(null, { emitEvent: false })
      businessIdControl?.disable({ emitEvent: false })
      nameControl?.disable({ emitEvent: false })
      // isStandardControl?.disable({ emitEvent: true })
      // isStandardControl?.setValue(true, { emitEvent: true })
    } else {
      businessIdControl?.setValidators(Validators.required)
      // businessIdControl?.enable({ emitEvent: true })
      // nameControl?.disable({ emitEvent: true })
      // isStandardControl?.disable({ emitEvent: false })
      // isStandardControl?.setValue(false, { emitEvent: false })
    }

    businessIdControl?.updateValueAndValidity({ onlySelf: true, emitEvent: false })
  }

  toggleAllPermissions(action: 'view' | 'add' | 'edit' | 'delete', checked: boolean) {
    for (const module of this.permissionModules) {
      if (!this.permissionsState[module.key]) {
        this.permissionsState[module.key] = {}
      }
      this.permissionsState[module.key][action] = checked
    }
  }

  // isAllChecked(action: 'view' | 'add' | 'edit' | 'delete'): boolean {
  //   return this.permissionModules.every(
  //     module => this.permissionsState[module.key]?.[action] === true
  //   )
  // }

  // togglePermission(module: any, action: string, value: boolean) {
  //   const key = module.key
  //   if (!this.permissionsState[module.key]) {
  //     this.permissionsState[module.key] = {}
  //   }
  //   // this.permissionsState[module.key][action] = value
  //   this.permissionsState[key][action as PermissionAction] = value
  // }
  // togglePermission(module: any, action: string, value: boolean): void {
  //   const key = module.key
  //   if (!this.permissionsState[module.key]) {
  //     this.permissionsState[module.key] = {}
  //   }

  //   // Set the selected action
  //   this.permissionsState[key][action as PermissionAction] = value

  //   // Auto-check 'view' if 'add', 'edit', or 'delete' is selected
  //   if ((action === 'add' || action === 'edit' || action === 'delete') && value) {
  //     this.permissionsState[module.key]['view'] = true
  //   }
  // }

  togglePermission(module: any, action: string, value: boolean): void {
    const key = module.key
    if (!this.permissionsState[key]) {
      this.permissionsState[key] = {}
    }

    // Set the selected action
    this.permissionsState[key][action as PermissionAction] = value

    // Auto-check 'view' if 'add', 'edit', or 'delete' is selected
    if ((action === 'add' || action === 'edit' || action === 'delete') && value) {
      this.permissionsState[key]['view'] = true
    }

    // If 'view' is unchecked, uncheck 'add', 'edit', and 'delete'
    if (action === 'view' && !value) {
      this.permissionsState[key]['add'] = false
      this.permissionsState[key]['edit'] = false
      this.permissionsState[key]['delete'] = false
    }
  }

  // Returns whether all checkboxes for an action are checked
  isAllChecked(action: 'view' | 'add' | 'edit' | 'delete'): boolean {
    return this.permissionModules.every(module => this.permissionsState[module.key]?.[action])
  }

  // Toggles all checkboxes for a column based on the header checkbox
  // toggleAllColumn(action: 'view' | 'add' | 'edit' | 'delete', checked: boolean): void {
  //   for (const module of this.permissionModules) {
  //     if (!this.permissionsState[module.key]) {
  //       this.permissionsState[module.key] = {}
  //     }

  //     // Always check 'view' if action is add/edit/delete
  //     if (['add', 'edit', 'delete'].includes(action) && checked) {
  //       this.permissionsState[module.key]['view'] = true
  //     }

  //     this.permissionsState[module.key][action] = checked
  //   }
  // }

  toggleAllColumn(action: 'view' | 'add' | 'edit' | 'delete', checked: boolean): void {
    for (const module of this.permissionModules) {
      if (!this.permissionsState[module.key]) {
        this.permissionsState[module.key] = {}
      }

      if (action === 'view') {
        // Set view permission
        this.permissionsState[module.key]['view'] = checked

        // If view is unchecked, remove all add/edit/delete
        if (!checked) {
          this.permissionsState[module.key]['add'] = false
          this.permissionsState[module.key]['edit'] = false
          this.permissionsState[module.key]['delete'] = false
        }
      } else {
        // For add/edit/delete, always check view if checked is true
        if (checked) {
          this.permissionsState[module.key]['view'] = true
        }
        this.permissionsState[module.key][action] = checked
      }
    }
  }

  getDashboardRoute(): string {
    // return this.user?.role.name === 'Admin' ? eps.dashboardPath : eps.employeeDashboardPath
    return this.utilService.getDashboardRoute()
  }

  customSearchFn = (term: string, item: any) => {
    const fullName = `${item.businessName}`.toLowerCase()
    return fullName.includes(term.toLowerCase())
  }
}
